/**
 * @file FundManagerDashboard.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useCallback, useEffect, useState } from "react";
import SkinPage from "../../SkinPage";
import StrategiesList from "../../widgets/trading/StrategiesList";
import "../../../../styles/fund-manager-dashboard.css";
import AllPendingRequests from "../../widgets/trading/AllPendingRequests";
import Request from "../../../../utils/fetchUtils";
import { getCurrentMonthFilter } from "../../../../utils/dateUtils";

function FundManagerDashboard() {
  const [strategies, setStrategies] = useState([]);
  const [strategiesLoading, setStrategiesLoading] = useState(true);

  // ADFE-143: fetched once here and shared with both children below —
  // AllPendingRequests and StrategiesList used to each call
  // GET /trading/strategy independently on the same page load.
  const fetchStrategies = useCallback(async () => {
    setStrategiesLoading(true);
    const res = await Request(`/trading/strategy?q=&${getCurrentMonthFilter()}`);
    if (res.ok) {
      setStrategies(res.data);
    }
    setStrategiesLoading(false);
  }, []);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  return (
    <SkinPage>
      <AllPendingRequests strategies={strategies} onRequestHandled={fetchStrategies} />
      <StrategiesList
        initialStrategies={strategies}
        initialStrategiesLoading={strategiesLoading}
      />
    </SkinPage>
  );
}

export default FundManagerDashboard;
